import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Trophy, Swords, Target, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PLAN_LABEL = { rookie: "Rookie", pro: "Pro", legend: "Legend", admin: "Administrator" };

export default function Profile() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm: "" });
  const [passwordBusy, setPasswordBusy] = useState(false);
  const targetId = id || (user && user.user_id);

  useEffect(() => {
    if (authLoading) return;
    if (!targetId) { navigate("/login", { state: { from: "/profile" } }); return; }
    fetch(`${API}/players/${targetId}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setData).catch(() => setData(false));
  }, [targetId, authLoading, navigate]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm) { toast.error("The new passwords do not match."); return; }
    setPasswordBusy(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: passwords.current_password, new_password: passwords.new_password }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || "Password change failed.");
      toast.success("Password changed.");
      setPasswords({ current_password: "", new_password: "", confirm: "" });
    } catch (error) { toast.error(error.message || "Could not change password."); } finally { setPasswordBusy(false); }
  };

  if (!data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-gp-blue" size={30} /></div>;
  if (data === false) return <div className="mx-auto max-w-3xl px-5 py-28 text-center"><h1 className="font-unbounded font-black text-3xl">Player not found</h1></div>;

  const { player, stats } = data;
  const cards = [
    { icon: Trophy, label: "Tournaments", value: stats.tournaments, color: "text-gp-gold" },
    { icon: Swords, label: "Matches", value: stats.matches, color: "text-gp-blue" },
    { icon: Target, label: "Wins", value: stats.wins, color: "text-gp-green" },
    { icon: Sparkles, label: "Points", value: player.points, color: "text-gp-magic" },
  ];

  return (
    <div className="mx-auto max-w-3xl px-5 md:px-8 py-12 md:py-16">
      <div className="glass rounded-3xl p-8 flex items-center gap-5">
        {player.picture ? <img src={player.picture} alt="" className="h-20 w-20 rounded-2xl object-cover" /> : <span className="grid place-items-center h-20 w-20 rounded-2xl bg-gp-blue/20 text-gp-blue font-unbounded font-black text-3xl">{(player.name || "?").slice(0, 1).toUpperCase()}</span>}
        <div>
          <h1 className="font-unbounded font-black tracking-tighter text-3xl">{player.name || "Player"}</h1>
          <span className="inline-flex items-center gap-1.5 mt-2 text-sm text-gp-gold bg-gp-gold/10 px-3 py-1 rounded-full"><Sparkles size={13} /> {PLAN_LABEL[player.plan] || "Rookie"} member</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6" data-testid="profile-stats">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="glass rounded-2xl p-5 text-center">
              <Icon className={`mx-auto ${c.color}`} size={22} />
              <p className="font-unbounded font-black text-3xl mt-2">{c.value}</p>
              <p className="text-xs text-white/50 uppercase tracking-widest mt-1">{c.label}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex gap-3">
        <Button onClick={() => navigate("/tournaments")} className="rounded-full bg-gp-blue font-bold px-6">Find a tournament</Button>
        {!id && <Button onClick={() => navigate("/pricing")} variant="outline" className="rounded-full border-white/20 bg-white/5 px-6">Manage plan</Button>}
      </div>

      {!id && user?.provider === "local" && (
        <form onSubmit={changePassword} className="glass rounded-3xl p-7 mt-8">
          <div className="flex items-center gap-2"><Lock size={19} className="text-gp-blue" /><h2 className="font-unbounded font-bold">Change password</h2></div>
          <div className="grid md:grid-cols-3 gap-3 mt-5">
            <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} placeholder="Current password" required className="bg-black/30 border-white/10 h-12 rounded-xl" />
            <Input type="password" minLength={8} value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} placeholder="New password" required className="bg-black/30 border-white/10 h-12 rounded-xl" />
            <Input type="password" minLength={8} value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} placeholder="Confirm new password" required className="bg-black/30 border-white/10 h-12 rounded-xl" />
          </div>
          <Button type="submit" disabled={passwordBusy} className="mt-4 rounded-full bg-gp-blue font-bold px-6">{passwordBusy ? <Loader2 className="animate-spin" size={18} /> : "Update password"}</Button>
        </form>
      )}
    </div>
  );
}
